// src/curated/originPlace.js
// Start cities on trip pages. Travellers can pick any airport or railway station from the search
// form's static lists (no Google), not only the fixed ORIGIN_CITIES.
// A pick is a place: { name, lat, lng, code?, kind? }. Trip state keeps originName as before and,
// for a place outside ORIGIN_CITIES, params.originPlace with its coordinates.

const num = (v) => (v === "" || v == null ? NaN : Number(v));
const norm = (s) => String(s || "").trim().toLowerCase();

/** A usable place (finite coordinates and a name), trimmed to what route search needs */
export function cleanPlace(p) {
    if (!p || !p.name) return null;
    const lat = num(p.lat), lng = num(p.lng);
    if (!Number.isFinite(lat) || !Number.isFinite(lng) || (lat === 0 && lng === 0)) return null;
    return { name: String(p.name).slice(0, 80), lat, lng, ...(p.code ? { code: String(p.code).slice(0, 8) } : {}), ...(p.kind ? { kind: p.kind } : {}) };
}

/** The trip's start city: the custom place when it matches originName, else the ORIGIN_CITIES entry */
export function originFrom(params, list, fallback = true) {
    const p = cleanPlace(params?.originPlace);
    if (p && norm(p.name) === norm(params?.originName)) return p;
    return (list || []).find((c) => norm(c.name) === norm(params?.originName)) || (fallback ? (list || [])[0] || null : null);
}

/** set_params fields for a pick (a name from the list, or a place from the autocomplete) */
export const originFields = (o) => (o && typeof o === "object" ? { origin: o.name, origin_place: cleanPlace(o) } : { origin: o });

/**
 * set_params origin handling shared by both trip editors. Mutates params; returns
 * { changed, name } or { error }.
 */
export function applyOrigin(params, o, origins) {
    const place = cleanPlace(o.origin_place);
    if (place) {
        const known = origins.find((x) => norm(x.name) === norm(place.name));
        if (known) {
            if (params.originName === known.name && !params.originPlace) return { changed: false };
            params.originName = known.name; delete params.originPlace;
            return { changed: true, name: known.name };
        }
        if (params.originName === place.name && params.originPlace && params.originPlace.lat === place.lat) return { changed: false };
        params.originName = place.name; params.originPlace = place;
        return { changed: true, name: place.name };
    }
    if (!o.origin) return { changed: false };
    // A name: the list, or the custom place already chosen (chat saying "from Jabalpur" again)
    const cur = cleanPlace(params.originPlace);
    if (cur && norm(cur.name) === norm(o.origin)) return { changed: false };
    const c = origins.find((x) => norm(x.name) === norm(o.origin)) || origins.find((x) => norm(x.name).includes(norm(o.origin)));
    if (!c) return { error: true };
    if (params.originName === c.name && !params.originPlace) return { changed: false };
    params.originName = c.name; delete params.originPlace;
    return { changed: true, name: c.name };
}

// "NEW DELHI (NDLS)" -> "New Delhi (NDLS)": the station and airport lists are in capitals
const tidy = (s) => String(s || "").replace(/^([^(]*)/, (m) => (m === m.toUpperCase() ? m.toLowerCase().replace(/\b([a-z])/g, (c) => c.toUpperCase()) : m));

/**
 * Place for an autocomplete pick, from the static lists only (no Google): airports carry
 * coordinates, stations get them from data/stationCoords.json (passed in as coords).
 */
export function placeFromSuggestion(item, coords = {}) {
    if (!item) return null;
    const label = tidy(item.label || item.name || item.description || "");
    let lat = num(item.lat), lng = num(item.lng);
    if ((!Number.isFinite(lat) || !Number.isFinite(lng)) && Array.isArray(item.lat_long)) { lat = num(item.lat_long[0]); lng = num(item.lat_long[1]); }
    if ((!Number.isFinite(lat) || !Number.isFinite(lng)) && item.type === "station" && coords[item.code]) [lat, lng] = coords[item.code];
    return cleanPlace({ name: label, lat, lng, code: item.code || "", kind: item.type });
}

/** A start place carried in a link: ?from=<name>&flat=..&flng=..&fcode=.. (a known city needs only from=) */
export function placeToQuery(q, o) {
    if (!o) return q;
    const p = typeof o === "object" ? cleanPlace(o) : null;
    q.set("from", p ? p.name : String(o));
    if (p) { q.set("flat", String(p.lat)); q.set("flng", String(p.lng)); if (p.code) q.set("fcode", p.code); }
    return q;
}
export const placeFromQuery = (q) => cleanPlace({ name: q.get("from") || "", lat: q.get("flat"), lng: q.get("flng"), code: q.get("fcode") || "" });
export const PLACE_QUERY_KEYS = ["from", "flat", "flng", "fcode"];
